import { LoadingButton } from "components";
import { motion } from "framer-motion";
import {
	FlexNetUploadTemplateButtonLabel,
	FlexNetUploadTemplateFileName,
	ResolveFlexNetTemplateSector,
} from "lib/flexnet/uploadTemplate";
import * as hooks from "lib/hooks";
import * as result from "lib/result";
import { LoadingButtonStyle } from "lib/styles/button";
import * as util from "lib/util";
import { Sector } from "models";
import { useState } from "react";

interface downloadExcelTemplateProps {
	selectedSector: Sector;
	/**
	 * When true, download Product Number | Serial template; when false, Serial-only.
	 * Defaults to true (fail closed) until the FlexNet PN gate resolves.
	 * Same boolean that drives the FlexNet warning (gate fails → with Part).
	 */
	partNumberRequired?: boolean;
}

export function DownloadExcelTemplate({
	selectedSector,
	partNumberRequired = true,
}: downloadExcelTemplateProps): React.JSX.Element {
	const mccApi = hooks.UseMccApi();
	const snack = hooks.UseSnackBar();
	const { theme } = hooks.UseTheme();
	const sector = Object.assign(new Sector(), selectedSector);
	const sectorType = ResolveFlexNetTemplateSector(sector.name);
	const fileName = FlexNetUploadTemplateFileName(
		sectorType,
		partNumberRequired
	);
	const buttonLabel = FlexNetUploadTemplateButtonLabel(
		sectorType,
		partNumberRequired
	);

	const [loading, setLoading] = useState(false);

	const handleDownload = async () => {
		setLoading(true);
		try {
			const blob = await mccApi.DownloadExcelTemplate(fileName);
			const url = window.URL.createObjectURL(blob);
			const a = document.createElement("a");
			a.href = url;
			a.download = fileName;
			document.body.appendChild(a);
			a.click();
			a.remove();
			window.URL.revokeObjectURL(url);

			snack.open(result.Ok("Template downloaded successfully"));
		} catch (e) {
			snack.open(result.Err(util.GetMsgFromError(e as Error)));
		} finally {
			setLoading(false);
		}
	};

	return (
		<motion.div transition={{ duration: 0.15 }}>
			<LoadingButton
				onClick={handleDownload}
				variant="contained"
				color="primary"
				loading={loading}
				sx={LoadingButtonStyle(theme)}
				data-test-selector="download-excel-template"
			>
				{buttonLabel}
			</LoadingButton>
		</motion.div>
	);
}
